import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "../../../../fetchSetup";
import { fetchUserFromRedis } from "../../AuthonticationScreens/Register";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const normalizeReport = (item, index) => {
  if (typeof item === "string") {
    return {
      id: `report-${index}`,
      title: item.file_name,
      url: item,
    };
  }

  if (item && typeof item === "object") {
    const url = item.cloudfront_url || item.cloudfrontUrl || item.url || item.link || item.file_url || "";
    return {
      id: item.booking_id || item._id || `report-${index}`,
      title: item.file_name || item.name || item.original_filename || item.filename,
      url,
      test_name:item.test_name
    };
  }

  return {
    id: item.booking_id,
    title: item.file_name,
    url: "",
    test_name:item.test_name
  };
};



function getFileIcon(name) {
  const ext = (name.split(".").pop() || "").toLowerCase();

  if (ext === "pdf") {
    return <PictureAsPdfOutlinedIcon sx={{ fontSize: 42, color: "#D32F2F" }} />;
  }

  return <InsertDriveFileOutlinedIcon sx={{ fontSize: 42, color: "#1976D2" }} />;
}

const DiagnosticReportsView = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [userEmail, setUserEmail] = useState("");

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    try {
      setLoading(true);
      setError("");

      const userId = sessionStorage.getItem("userId");
      const token = sessionStorage.getItem("token");

      if (!userId || !token) {
        setError("Please log in to view your diagnostic reports.");
        return;
      }

      const user = await fetchUserFromRedis(userId, token);
      const email = user?.email || "";
      setUserEmail(email);

      if (!email) {
        setError("We could not find your email address for the reports.");
        return;
      }

      const response = await apiRequest(`${API_BASE}/booking/user-diagnostic-reports/${encodeURIComponent(email)}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data?.error || "Failed to load diagnostic reports.");
      }

      const reportList = Array.isArray(data?.report_list) ? data.report_list : [];
      console.log(reportList)
      setReports(reportList.map((item, index) => normalizeReport(item, index)));
    } catch (err) {
      console.error("Error loading diagnostic reports:", err);
      setError(err.message || "Something went wrong while loading reports.");
    } finally {
      setLoading(false);
    }
  };


  const handleDownloadReport = (url, title) => {
    if (!url) return;

    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.download = title || "diagnostic-report";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const hasReports = useMemo(() => reports.some((report) => Boolean(report.url)), [reports]);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={0} sx={{ p: { xs: 2.5, md: 4 }, borderRadius: 4, border: "1px solid #ECECEC" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="h5" fontWeight={700} color="#111827">
              Diagnostic Reports
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {userEmail ? `Showing reports for ${userEmail}` : "View and download all your uploaded diagnostic reports."}
            </Typography>
          </Box>

          <Button
            variant="outlined"
            onClick={() => navigate("/profile-dashboard")}
            sx={{ borderColor: "#006D77", color: "#006D77", textTransform: "none" }}
          >
            Back to Profile
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress sx={{ color: "#006D77" }} />
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <DescriptionOutlinedIcon sx={{ fontSize: 48, color: "#9CA3AF" }} />
            <Typography variant="h6" sx={{ mt: 1, fontWeight: 600 }}>
              {error}
            </Typography>
          </Box>
        ) : !hasReports ? (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <DescriptionOutlinedIcon sx={{ fontSize: 48, color: "#9CA3AF" }} />
            <Typography variant="h6" sx={{ mt: 1, fontWeight: 600 }}>
              No diagnostic reports found yet.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Reports will appear here once they are uploaded for your bookings.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2.5}>
            {reports.filter((report) => Boolean(report.url)).map((report) => (
              <Grid item xs={12} md={6} lg={4} key={report.id}>
                <Card elevation={0} sx={{ border: "1px solid #E5E7EB", borderRadius: 3, height: "100%" }}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                      {getFileIcon(report.title)}
                      <Box>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#111827" }}>
                          {report.title}
                        </Typography>
                        <Chip
                          label={report.test_name}
                          size="small"
                          sx={{ mt: 0.5, bgcolor: "#E0F2F1", color: "#006D77", fontWeight: 600 }}
                        />
                        <Chip
                          label={report.id}
                          size="small"
                          sx={{ mt: 0.5, bgcolor: "#E0F2F1", color: "#006D77", fontWeight: 600 }}
                        />
                      </Box>
                    </Box>

                    <Stack direction="row" spacing={1.2} sx={{ mt: 2 }}>
                     
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<DownloadOutlinedIcon />}
                        onClick={() => handleDownloadReport(report.url, report.title)}
                        sx={{ textTransform: "none", bgcolor: "#006D77", "&:hover": { bgcolor: "#005761" } }}
                      >
                        Download
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>
    </Container>
  );
};

export default DiagnosticReportsView;
